/**
 * CapacityTaskRepository — OutpostForge frontend (CES standard)
 * Repository pattern with injected service (DIP) for the capacity task
 * lifecycle: dry-run/apply, monitoring, cancellation, blocking instances.
 */

import type { CapacityTaskService } from '../services/api/capacityTaskService';
import { capacityTaskService } from '../services/api/capacityTaskService';
import type {
  BlockingInstance,
  CapacityTaskResponse,
  CapacityTaskSummary,
  ListCapacityTasksResponse,
  StartCapacityTaskRequest,
} from '../types/outposts';

export class CapacityTaskRepository {
  constructor(private readonly service: CapacityTaskService) {}

  start(outpostId: string, request: StartCapacityTaskRequest): Promise<CapacityTaskResponse> {
    return this.service.startCapacityTask(outpostId, request);
  }

  async list(filters?: {
    outpostId?: string;
    statuses?: readonly string[];
  }): Promise<CapacityTaskSummary[]> {
    const response = await this.service.listCapacityTasks(filters);
    return response.tasks;
  }

  /** Like list(), but keeps the truncation metadata (nextToken/truncated). */
  listWithMeta(filters?: {
    outpostId?: string;
    statuses?: readonly string[];
  }): Promise<ListCapacityTasksResponse> {
    return this.service.listCapacityTasks(filters);
  }

  get(outpostId: string, taskId: string): Promise<CapacityTaskResponse> {
    return this.service.getCapacityTask(outpostId, taskId);
  }

  cancel(outpostId: string, taskId: string): Promise<unknown> {
    return this.service.cancelCapacityTask(outpostId, taskId);
  }

  async getBlockingInstances(outpostId: string, taskId: string): Promise<BlockingInstance[]> {
    const response = await this.service.getBlockingInstances(outpostId, taskId);
    return response.instances;
  }
}

export const CapacityTaskRepositoryFactory = {
  create(service: CapacityTaskService = capacityTaskService): CapacityTaskRepository {
    return new CapacityTaskRepository(service);
  },
};

/** Default instance used by the hooks. */
export const capacityTaskRepository = CapacityTaskRepositoryFactory.create();
